import React, { useMemo, useState } from 'react';
import ServiceScheduleShiftForm, {
	REPEAT_INTERVAL_OPTIONS,
	ServiceScheduleShiftFormValues,
} from '../ServiceScheduleShiftForm/ServiceScheduleShiftForm';
import { useServiceScheduleContext } from '../../../WorkplaceMember/Services/ServiceScheduleContext';
import useTranslation from '../../../../Hooks/useTranslation';
import Loading from '../../../../Components/Loading';
import {
	ServiceSchedule,
	ServiceScheduleItem,
	ServiceScheduleItemMember,
	ServiceScheduleType,
} from '../../../../graphql/graphql';
import { getMomentDate } from '../../../../Utils/dates.util';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { RepeatInterval } from '../../Enums/RepeatInterval.enum';
import useServiceSchedulesHelper from '../../Hooks/useServiceSchedulesHelper';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import { Moment } from 'moment';
import moment from 'moment';

interface Props {
	selectedDate: Moment;
	serviceSchedule: ServiceSchedule;
	serviceScheduleItem: ServiceScheduleItem;
	onSuccess: () => void;
	onDelete: () => void;
}

const ServiceScheduleUpdate: React.FC<Props> = ({
	onSuccess,
	selectedDate,
	serviceSchedule,
	serviceScheduleItem,
	onDelete,
}) => {
	const { workplaceMembersSelectValues, workplaceId } =
		useServiceScheduleContext();
	const { updateServiceSchedule } = useServiceSchedulesHelper();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [isUpdateOptionsModalOpen, setIsUpdateOptionsModalOpen] =
		useState(false);
	const [formValues, setFormValues] = useState<
		ServiceScheduleShiftFormValues | undefined
	>(undefined);

	const handleUpdate = async (
		values: ServiceScheduleShiftFormValues,
		isRepeatableSchedule: boolean,
	) => {
		if (isRepeatableSchedule) {
			setFormValues(values);
			setIsUpdateOptionsModalOpen(true);
		} else {
			await handleProcessUpdate(values, false);
		}
	};

	const handleProcessUpdate = async (
		values: ServiceScheduleShiftFormValues,
		updateFuture: boolean,
	) => {
		setIsLoading(true);
		const result = await updateServiceSchedule({
			workplaceId,
			values: values as ServiceScheduleShiftFormValues,
			updateFuture,
			serviceScheduleId: serviceSchedule.id,
			serviceScheduleItemId: serviceScheduleItem.id,
		});
		if (result) {
			onSuccess();
		}
		setIsLoading(false);
	};

	const prepareWorkplaceMembers = (
		members: ServiceScheduleItemMember[],
	): SelectValue | undefined => {
		return workplaceMembersSelectValues.find(
			i => i.value === members?.[0].workplaceMemberId,
		);
	};

	const defaultValues: ServiceScheduleShiftFormValues = useMemo(() => {
		const priceWithVatRaw: number | undefined = serviceScheduleItem.price
			?.priceWithVatRaw
			? parseFloat(serviceScheduleItem.price?.priceWithVatRaw)
			: undefined;

		const repeatIntervalDefaultValue: SelectValue | undefined =
			REPEAT_INTERVAL_OPTIONS.find(item => item.value === RepeatInterval.NEVER);

		return {
			date: getMomentDate(selectedDate),
			extraPayment: priceWithVatRaw ? priceWithVatRaw > 0 : false,
			individualTime: false,
			guaranteedTime: serviceScheduleItem.interval as number,
			price: priceWithVatRaw?.toString(),
			repeatInterval: {
				label: t(repeatIntervalDefaultValue?.label || ''),
				value: repeatIntervalDefaultValue?.value as string,
			},
			timeFrom: moment(serviceScheduleItem.timeFrom, 'HH:mm'),
			timeTo: moment(serviceScheduleItem.timeTo, 'HH:mm'),
			workingTime: serviceSchedule.type ?? ServiceScheduleType.Shift,
			workplaceMember: prepareWorkplaceMembers(
				serviceScheduleItem.serviceScheduleItemMembers as ServiceScheduleItemMember[],
			),
			repeatable: serviceScheduleItem.repeatable,
		};
	}, [serviceSchedule, serviceScheduleItem]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<>
			<ServiceScheduleShiftForm
				onSubmit={(values: ServiceScheduleShiftFormValues) =>
					handleUpdate(values, serviceScheduleItem.repeatable ?? false)
				}
				defaultValues={defaultValues}
				submitButtonText='base.shared.edit'
				onDelete={onDelete}
				isEdit
			/>
			{isUpdateOptionsModalOpen && formValues && (
				<ConfirmationModal
					title='base.shared.edit'
					text={t('component.schedule.editMultipleScheduleConfirmationMessage')}
					isOpen
					onClose={() => setIsUpdateOptionsModalOpen(false)}>
					<div
						className='d-flex flex-row align-items-center gap-10'
						style={{ gap: '20px' }}>
						<Button
							onClick={() => handleProcessUpdate(formValues, false)}
							theme={Theme.GREEN}
							title='base.editOnlyThisOne'
						/>
						<Button
							onClick={() => handleProcessUpdate(formValues, true)}
							theme={Theme.GREEN}
							title='base.editAlsoFutures'
						/>
					</div>
				</ConfirmationModal>
			)}
		</>
	);
};

export default ServiceScheduleUpdate;
